import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, X, Trash2, Loader2, Music, Video, Play, Pause, Square, Maximize2, Film, Scissors, GripVertical, Lightbulb, RotateCcw, SkipBack, SkipForward, Volume2, VolumeX } from 'lucide-react';
import toast from 'react-hot-toast';
import { createClip } from '../../../../services/clipsService';

// Two-tier picker: "Video" maps to the host's preferred layout (focus by
// default — most common case for clips of someone speaking). "Audio" is
// the .mp3 export. Advanced users can flip into the per-layout picker via
// the small "Change layout" link.
const SIMPLE_OPTIONS = [
  { value: 'video', label: 'Video', icon: Video, description: 'Trimmed video (.mp4)' },
  { value: 'audio_only', label: 'Audio only', icon: Music, description: 'Audio track (.mp3)' },
];
const VIDEO_LAYOUT_LABELS = {
  gallery_view: 'Gallery',
  focus: 'Focus',
  speaker_top: 'Speaker Top',
  speaker_side: 'Speaker Side',
};

const MIN_SEGMENT_MS = 500;
const DEFAULT_SEGMENT_MS = 15000;     // 15s default — better starter range than 5s

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n));
}

function formatTime(ms) {
  if (!isFinite(ms) || ms < 0) ms = 0;
  const total = Math.round(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Pick a sensible tick interval (in ms) for the time ruler given the total
 * duration. Aims for ~6–10 visible labels regardless of clip length so the
 * ruler stays readable for a 12-second recording or a 60-minute one.
 */
function pickTickIntervalMs(durationMs) {
  const candidates = [
    500, 1000, 2000, 5000, 10_000, 15_000, 30_000,
    60_000, 120_000, 300_000, 600_000, 1_800_000,
  ];
  const target = durationMs / 8;
  return candidates.find((c) => c >= target) || 1_800_000;
}

/**
 * Translate a source-video time + active-segment-index into elapsed time
 * within the composed clip output. Used by the scrubber so the time
 * display reads "0:08 / 0:15" against the FINAL clip duration, not the
 * source recording.
 */
function clipElapsedMs(sorted, idx, sourceTimeMs) {
  if (!sorted || sorted.length === 0 || idx < 0) return 0;
  let acc = 0;
  for (let i = 0; i < idx; i++) {
    acc += Math.max(0, sorted[i].endMs - sorted[i].startMs);
  }
  const seg = sorted[idx];
  if (seg) {
    acc += Math.max(0, Math.min(sourceTimeMs, seg.endMs) - seg.startMs);
  }
  return acc;
}

/**
 * Generate a fixed number of thumbnail data-URLs from a video URL by seeking
 * to evenly-spaced positions and drawing each frame to a canvas.
 *
 * Failure modes & how we handle them:
 *   - Seek that never completes (slow network, codec quirk) → 5s timeout
 *     per index, then we skip that slot and keep going.
 *   - drawImage throws (canvas tainted by CORS, codec mismatch) → log,
 *     skip that index, keep going.
 *   - Video element errors out → save whatever partial set we collected.
 *
 * We also wait one rAF tick after `onseeked` because Chrome occasionally
 * paints the new frame slightly after the event fires.
 *
 * Cached on the URL so flipping layout / re-opening the editor for the
 * same recording doesn't re-extract the same set repeatedly.
 */
const _thumbnailCache = new Map();
function useVideoThumbnails(videoUrl, count = 24) {
  const [thumbs, setThumbs] = React.useState(() => _thumbnailCache.get(`${videoUrl}|${count}`) || []);
  const [loading, setLoading] = React.useState(() => {
    if (!videoUrl) return false;
    return !_thumbnailCache.has(`${videoUrl}|${count}`);
  });
  const [progress, setProgress] = React.useState(0);

  React.useEffect(() => {
    if (!videoUrl) { setThumbs([]); setLoading(false); return; }
    const cacheKey = `${videoUrl}|${count}`;
    if (_thumbnailCache.has(cacheKey)) {
      setThumbs(_thumbnailCache.get(cacheKey));
      setLoading(false);
      setProgress(1);
      return;
    }
    setLoading(true);
    setProgress(0);

    let cancelled = false;
    let timeoutHandle = null;
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.muted = true;
    // 'metadata' is lighter than 'auto' for long videos — we only need headers
    // + range-fetched chunks at each seek. Browsers handle the rest via Range.
    video.preload = 'auto';
    video.src = videoUrl;
    video.playsInline = true;

    const canvas = document.createElement('canvas');
    canvas.width = 160;
    canvas.height = 90;
    const ctx = canvas.getContext('2d');

    const out = new Array(count).fill(null);
    let i = 0;

    const finish = () => {
      if (cancelled) return;
      cancelled = true;
      if (timeoutHandle) { clearTimeout(timeoutHandle); timeoutHandle = null; }
      const filtered = out.filter(Boolean);
      if (filtered.length > 0) _thumbnailCache.set(cacheKey, filtered);
      setThumbs(filtered);
      setLoading(false);
      setProgress(1);
    };

    const captureNext = () => {
      if (cancelled) return;
      if (i >= count) { finish(); return; }
      const dur = video.duration;
      if (!isFinite(dur) || dur <= 0) {
        console.warn('[Thumbnails] Invalid duration — bailing');
        finish();
        return;
      }
      const slotWidth = dur / count;
      const target = Math.min(dur - 0.05, i * slotWidth + slotWidth / 2);

      // Per-seek timeout — if onseeked doesn't fire in 5s for whatever
      // reason, skip this slot and keep going.
      if (timeoutHandle) clearTimeout(timeoutHandle);
      timeoutHandle = setTimeout(() => {
        console.warn(`[Thumbnails] seek timeout at index ${i} (target ${target.toFixed(2)}s) — skipping`);
        i++;
        captureNext();
      }, 5000);

      try { video.currentTime = target; } catch (err) {
        console.warn('[Thumbnails] currentTime assignment failed:', err.message);
        i++;
        captureNext();
      }
    };

    video.onloadedmetadata = () => {
      if (cancelled) return;
      console.log(`[Thumbnails] Loaded ${videoUrl.split('/').pop()} — ${video.duration.toFixed(1)}s, extracting ${count} frames`);
      captureNext();
    };

    video.onseeked = () => {
      if (cancelled) return;
      if (timeoutHandle) { clearTimeout(timeoutHandle); timeoutHandle = null; }
      // Wait one paint tick — Chrome sometimes fires onseeked before the
      // new frame is actually composited into the video element.
      requestAnimationFrame(() => {
        if (cancelled) return;
        try {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          out[i] = canvas.toDataURL('image/jpeg', 0.55);
        } catch (err) {
          // Tainted canvas (CORS / codec). Don't kill the whole extraction
          // — log and skip this index. If ALL fail we'll still finish() with
          // an empty array, which falls back to the gradient.
          console.warn(`[Thumbnails] drawImage failed at index ${i}:`, err?.message || err);
        }
        i++;
        setProgress(i / count);
        // Update mid-extraction so the user sees progress on long videos.
        // Only push the data array every 4 frames to avoid 24 re-renders.
        if (i % 4 === 0) {
          const partial = out.filter(Boolean);
          if (partial.length > 0) setThumbs(partial);
        }
        setTimeout(captureNext, 0);
      });
    };

    video.onerror = () => {
      const code = video.error?.code;
      const msg = video.error?.message;
      console.warn(`[Thumbnails] video errored (code=${code}): ${msg || 'unknown'}`);
      finish();
    };

    return () => {
      cancelled = true;
      if (timeoutHandle) clearTimeout(timeoutHandle);
      video.removeAttribute('src');
      try { video.load(); } catch (_) { /* ignore */ }
    };
  }, [videoUrl, count]);

  return { thumbs, loading, progress };
}

/**
 * Renders a horizontal filmstrip of pre-extracted thumbnails covering the
 * portion of the source video between startMs and endMs. Falls back to a
 * gradient if no thumbnails are available (e.g. while extraction is still
 * running, or if CORS/codec extraction failed).
 */
function ThumbnailStrip({ thumbnails, startMs, endMs, totalDurationMs, loading, progress }) {
  // Background gradient is the underlay for both "still loading" and "no
  // thumbnails available" states.
  const showShimmer = loading && (!thumbnails || thumbnails.length === 0);
  if (showShimmer) {
    return (
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#6a1fbf]/70 to-[#00c853]/70" />
        {/* Animated shimmer sweep so the user can tell extraction is in flight */}
        <div
          className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/15 to-transparent animate-[shimmer_1.6s_infinite]"
          style={{
            animation: 'clip-shimmer 1.6s ease-in-out infinite',
          }}
        />
        <style>{`@keyframes clip-shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(400%); } }`}</style>
        {typeof progress === 'number' && progress > 0 && (
          <div
            className="absolute bottom-0 left-0 h-0.5 bg-amber-300/80 transition-[width]"
            style={{ width: `${Math.min(100, progress * 100)}%` }}
          />
        )}
      </div>
    );
  }
  if (!thumbnails || thumbnails.length === 0 || totalDurationMs <= 0) {
    return (
      <div className="absolute inset-0 bg-gradient-to-r from-[#6a1fbf]/70 to-[#00c853]/70" />
    );
  }
  const slotWidth = totalDurationMs / thumbnails.length;
  const startIdx = Math.max(0, Math.floor(startMs / slotWidth));
  const endIdx = Math.min(thumbnails.length, Math.ceil(endMs / slotWidth));
  const slice = thumbnails.slice(startIdx, Math.max(endIdx, startIdx + 1));
  return (
    <div className="absolute inset-0 flex overflow-hidden">
      {slice.map((src, i) => (
        <img
          key={`${startIdx}-${i}`}
          src={src}
          alt=""
          className="h-full flex-1 min-w-0 object-cover"
          draggable={false}
        />
      ))}
    </div>
  );
}

/**
 * Scrubber bar for the preview pane — solid track with a colored fill up
 * to the current position. Click anywhere on it to seek that point in the
 * composed clip; the parent translates clip-relative ms back into a
 * source-video seek.
 */
function ClipScrubber({ previewing, previewIdx, previewSortedRef, previewTimeMs, totalDurationMs, onSeekClipMs }) {
  const trackRef = React.useRef(null);
  const elapsed = previewing
    ? clipElapsedMs(previewSortedRef.current, previewIdx, previewTimeMs)
    : 0;
  const pct = totalDurationMs > 0 ? Math.min(100, (elapsed / totalDurationMs) * 100) : 0;

  const handleClick = (e) => {
    if (totalDurationMs <= 0) return;
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return;
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    onSeekClipMs?.(ratio * totalDurationMs);
  };

  return (
    <div
      ref={trackRef}
      onClick={handleClick}
      className="relative flex-1 h-1.5 rounded-full bg-white/15 cursor-pointer group"
      title="Click to scrub the preview"
    >
      <div
        className="absolute top-0 left-0 h-full rounded-full bg-gradient-to-r from-[#6a1fbf] to-[#00c853]"
        style={{ width: `${pct}%` }}
      />
      {/* Playhead nub */}
      <div
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-white shadow opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
        style={{ left: `${pct}%` }}
      />
    </div>
  );
}

/**
 * Re-sort segments by start. Returns a new array.
 * Caller is responsible for overlap validation.
 */
function sortSegments(segments) {
  return [...segments].sort((a, b) => a.startMs - b.startMs);
}

/**
 * Find first index where inserting `seg` would cause an overlap with the
 * existing (sorted, non-overlapping) segments. Returns -1 if no overlap.
 */
function findOverlap(segments, seg, ignoreIndex = -1) {
  for (let i = 0; i < segments.length; i++) {
    if (i === ignoreIndex) continue;
    const s = segments[i];
    if (seg.startMs < s.endMs && seg.endMs > s.startMs) return i;
  }
  return -1;
}

/**
 * ClipEditor — inline timeline editor that lives below the existing player.
 *
 * Props:
 *   parentRecordingId, durationMs, currentTimeMs, onSeek(ms), onClose(), onSaved(clip)
 *   availableLayouts: string[]   (from parent Recording; controls which video options show)
 */
export default function ClipEditor({
  parentRecordingId,
  durationMs,
  currentTimeMs,
  onSeek,
  onClose,
  onSaved,
  availableLayouts = [],
  recordingAssets = [],
  fallbackVideoUrl,
}) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  // Quick output: 'video' or 'audio_only'. The actual sub-layout (focus / gallery /
  // etc) is held separately so users can collapse/expand the advanced picker.
  const [outputKind, setOutputKind] = useState('video');
  const [showLayoutPicker, setShowLayoutPicker] = useState(false);

  // Pick the best default video sub-layout for this recording.
  const defaultVideoLayout = useMemo(() => {
    if (availableLayouts.includes('focus')) return 'focus';
    if (availableLayouts.includes('gallery_view')) return 'gallery_view';
    return availableLayouts.find((l) => l !== 'audio_only') || 'gallery_view';
  }, [availableLayouts]);
  const [videoLayout, setVideoLayout] = useState(defaultVideoLayout);
  useEffect(() => { setVideoLayout(defaultVideoLayout); }, [defaultVideoLayout]);

  // Final layout sent to the backend.
  const layout = outputKind === 'audio_only' ? 'audio_only' : videoLayout;

  // Pre-seed a single segment that spans the entire source recording. The
  // user can then drag the handles in, hit Split to break it apart, or
  // Start over to wipe and rebuild — same mental model as Capcut / Movavi
  // where you start with the whole clip and trim down.
  const [segments, setSegments] = useState(() => {
    if (!durationMs) return [];
    return [{ startMs: 0, endMs: Math.round(durationMs) }];
  });

  const timelineRef = useRef(null);
  // Tracks the active drag op so onMouseMove on the timeline can update the
  // right segment edge. { index, edge: 'start'|'end', startX, originalMs } or null.
  const dragStateRef = useRef(null);
  // Suppresses the click event that fires after mouseup ends a drag — without
  // this, every drag-release also fired handleTimelineClick and seeked the
  // player to the release point.
  const justDraggedRef = useRef(false);

  // Live tooltip shown above the playhead while a handle is being dragged.
  // Lets the user see exactly what time they're snapping to without having
  // to look at the segments-list footer.
  const [dragTip, setDragTip] = useState(null); // { leftPct, label } | null

  // Dismissible onboarding hint — shows on first use, hidden after the user
  // either clicks dismiss or successfully saves a clip.
  const [showHint, setShowHint] = useState(() => {
    if (typeof window === 'undefined') return true;
    try { return !window.localStorage.getItem('clipEditorHintDismissed'); } catch (_) { return true; }
  });
  const dismissHint = useCallback(() => {
    setShowHint(false);
    try { window.localStorage.setItem('clipEditorHintDismissed', '1'); } catch (_) { /* ignore */ }
  }, []);

  // Preview state — the editor renders its OWN <video> element below the
  // timeline and plays through the user's segments back-to-back inside it.
  // The recording's main player above is left alone. previewIdx is the
  // index of the currently-playing segment in the sortSegments(segments) order.
  const previewVideoRef = useRef(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewIdx, setPreviewIdx] = useState(-1);
  const [previewTimeMs, setPreviewTimeMs] = useState(0);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [previewMuted, setPreviewMuted] = useState(false);
  const previewSortedRef = useRef([]);   // snapshot of sorted segments at preview start

  // Editor-internal playhead — the SOURCE of truth for "where the user is
  // looking inside the editor". The parent recording's player at the top of
  // the page has its own position which we don't want to track once the
  // editor is open. The preview video drives this via onTimeUpdate; clicking
  // the timeline also updates it (and seeks the preview).
  const [editorPlayheadMs, setEditorPlayheadMs] = useState(currentTimeMs || 0);

  // If the user scrubs the parent recording's player while the editor is
  // open AND the preview pane isn't actively playing, mirror that into the
  // editor playhead so Split / Add-segment still feel responsive. Once the
  // user starts using the preview, the preview's onTimeUpdate takes over.
  useEffect(() => {
    if (previewing) return;
    setEditorPlayheadMs(currentTimeMs || 0);
  }, [currentTimeMs, previewing]);

  const totalDurationMs = useMemo(
    () => segments.reduce((sum, s) => sum + Math.max(0, s.endMs - s.startMs), 0),
    [segments],
  );

  // Available video sub-layouts (filter-by what the recording actually has).
  const availableVideoLayouts = useMemo(
    () => availableLayouts.filter((l) => l !== 'audio_only' && VIDEO_LAYOUT_LABELS[l]),
    [availableLayouts],
  );

  // Resolve the playback URL for the preview pane based on whichever layout
  // the user has currently chosen. Falls back to the recording's currently-
  // playing URL if no per-layout asset is registered (e.g. legacy recordings).
  const previewSourceUrl = useMemo(() => {
    if (outputKind === 'audio_only') {
      const a = recordingAssets.find((x) => x.assetType === 'audio_only' && x.fileUrl);
      if (a) return a.fileUrl;
      // Fall back to a video layout — browsers can play just the audio track.
      const v = recordingAssets.find((x) => x.assetType === videoLayout && x.fileUrl)
        || recordingAssets.find((x) => x.fileUrl);
      return v?.fileUrl || fallbackVideoUrl || '';
    }
    const v = recordingAssets.find((x) => x.assetType === videoLayout && x.fileUrl);
    return v?.fileUrl || fallbackVideoUrl || '';
  }, [outputKind, videoLayout, recordingAssets, fallbackVideoUrl]);

  // Pre-extract thumbnails of the source video so segment blocks render a
  // real filmstrip (like the reference editor) instead of a plain gradient.
  // 24 thumbs is enough resolution for most clips while staying cheap.
  const { thumbs: thumbnails, loading: thumbsLoading, progress: thumbsProgress } = useVideoThumbnails(
    outputKind !== 'audio_only' ? previewSourceUrl : null,
    24,
  );

  // ── Adding segments ─────────────────────────────────────────
  const addSegmentAtPlayhead = useCallback(() => {
    if (!durationMs) return;
    const start = clamp(editorPlayheadMs, 0, Math.max(0, durationMs - MIN_SEGMENT_MS));
    let end = clamp(start + DEFAULT_SEGMENT_MS, start + MIN_SEGMENT_MS, durationMs);

    // Snap end down to avoid overlapping the next segment (if any).
    const sorted = sortSegments(segments);
    for (const s of sorted) {
      if (s.startMs > start && s.startMs < end) {
        end = s.startMs;
        break;
      }
    }
    if (end - start < MIN_SEGMENT_MS) {
      toast.error('Not enough room for a segment here');
      return;
    }

    const newSeg = { startMs: start, endMs: end };
    if (findOverlap(sorted, newSeg) !== -1) {
      toast.error('Segment overlaps an existing one');
      return;
    }
    setSegments(sortSegments([...sorted, newSeg]));
  }, [editorPlayheadMs, durationMs, segments]);

  const removeSegment = (index) => {
    setSegments((prev) => prev.filter((_, i) => i !== index));
  };

  // ── Drag handles (start / end / move-whole-segment) ─────────
  // edge: 'start' resizes the in-point, 'end' resizes the out-point,
  //       'move' shifts the whole segment left/right (duration preserved).
  const beginDrag = (index, edge, e) => {
    e.preventDefault();
    e.stopPropagation();
    const seg = segments[index];
    if (!seg) return;
    dragStateRef.current = {
      index,
      edge,
      startX: e.clientX,
      originalStart: seg.startMs,
      originalEnd: seg.endMs,
    };
    document.addEventListener('mousemove', handleDragMove);
    document.addEventListener('mouseup', endDrag);
  };

  const handleDragMove = useCallback((e) => {
    const ds = dragStateRef.current;
    const bar = timelineRef.current;
    if (!ds || !bar) return;
    const rect = bar.getBoundingClientRect();
    const pxPerMs = rect.width / Math.max(1, durationMs);
    const deltaMs = (e.clientX - ds.startX) / pxPerMs;
    // Snap to whole seconds while dragging unless the user holds Shift to
    // disable snapping for fine adjustments. Layperson-friendly default.
    const snap = !e.shiftKey;
    const snapMs = (ms) => snap ? Math.round(ms / 1000) * 1000 : Math.round(ms);

    let updatedSeg = null;
    setSegments((prev) => {
      const next = [...prev];
      const seg = { ...next[ds.index] };
      if (ds.edge === 'start') {
        seg.startMs = clamp(snapMs(ds.originalStart + deltaMs), 0, ds.originalEnd - MIN_SEGMENT_MS);
      } else if (ds.edge === 'end') {
        seg.endMs = clamp(snapMs(ds.originalEnd + deltaMs), ds.originalStart + MIN_SEGMENT_MS, durationMs);
      } else {
        // 'move' — shift both by the same delta, preserving duration. Clamp
        // to [0, durationMs] so the segment never falls off the timeline.
        const segLen = ds.originalEnd - ds.originalStart;
        let newStart = snapMs(ds.originalStart + deltaMs);
        if (newStart < 0) newStart = 0;
        if (newStart + segLen > durationMs) newStart = durationMs - segLen;
        seg.startMs = newStart;
        seg.endMs = newStart + segLen;
      }
      // Block overlap with neighbors
      if (findOverlap(next, seg, ds.index) !== -1) return prev;
      next[ds.index] = seg;
      updatedSeg = seg;
      return next;
    });

    // Live tooltip — show the time the active handle is currently anchored to.
    if (updatedSeg) {
      const showMs = ds.edge === 'end' ? updatedSeg.endMs : updatedSeg.startMs;
      setDragTip({
        leftPct: (showMs / Math.max(1, durationMs)) * 100,
        label: formatTime(showMs),
        edge: ds.edge,
      });
    }
  }, [durationMs]);

  const endDrag = useCallback(() => {
    const wasDragging = !!dragStateRef.current;
    dragStateRef.current = null;
    setDragTip(null);
    document.removeEventListener('mousemove', handleDragMove);
    document.removeEventListener('mouseup', endDrag);
    setSegments((prev) => sortSegments(prev));
    if (wasDragging) {
      // The native click event fires AFTER mouseup; mark that a drag just
      // happened so handleTimelineClick suppresses the resulting seek.
      justDraggedRef.current = true;
      setTimeout(() => { justDraggedRef.current = false; }, 0);
    }
  }, [handleDragMove]);

  // Cleanup on unmount in case drag was mid-flight
  useEffect(() => () => {
    document.removeEventListener('mousemove', handleDragMove);
    document.removeEventListener('mouseup', endDrag);
  }, [handleDragMove, endDrag]);

  // ── Preview controls (internal <video>) ────────────────────
  const stopPreview = useCallback(() => {
    setPreviewing(false);
    setPreviewIdx(-1);
    setPreviewPlaying(false);
    previewSortedRef.current = [];
    const v = previewVideoRef.current;
    if (v) {
      try { v.pause(); } catch (_) { /* ignore */ }
    }
  }, []);

  const startPreview = useCallback(() => {
    const sorted = sortSegments(segments);
    if (sorted.length === 0) {
      toast.error('Add at least one segment to preview');
      return;
    }
    if (!previewSourceUrl) {
      toast.error('No source video available to preview');
      return;
    }
    previewSortedRef.current = sorted;
    setPreviewing(true);
    setPreviewIdx(0);
    const v = previewVideoRef.current;
    if (v) {
      try {
        v.currentTime = sorted[0].startMs / 1000;
        const p = v.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
        setPreviewPlaying(true);
      } catch (_) { /* ignore */ }
    }
  }, [segments, previewSourceUrl]);

  // Watch the preview video's reported time and jump to the next segment
  // when the current one ends. Tolerance band of 50ms because timeupdate
  // fires every ~250ms.
  useEffect(() => {
    if (!previewing || previewIdx < 0) return;
    const sorted = previewSortedRef.current;
    const seg = sorted[previewIdx];
    if (!seg) return;
    if (previewTimeMs >= seg.endMs - 50) {
      const nextIdx = previewIdx + 1;
      if (nextIdx < sorted.length) {
        setPreviewIdx(nextIdx);
        const v = previewVideoRef.current;
        if (v) {
          try { v.currentTime = sorted[nextIdx].startMs / 1000; } catch (_) { /* ignore */ }
        }
      } else {
        // Last segment finished — pause and exit preview mode.
        stopPreview();
      }
    }
  }, [previewTimeMs, previewing, previewIdx, stopPreview]);

  // If user changes segments mid-preview, abort so they don't watch a stale render.
  useEffect(() => {
    if (previewing) stopPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segments.length]);

  // If user changes the layout/output mid-preview, abort (source URL changed).
  useEffect(() => {
    if (previewing) stopPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewSourceUrl]);

  // Handle <video> events
  const handlePreviewTimeUpdate = (e) => {
    const ms = Math.round(e.target.currentTime * 1000);
    setPreviewTimeMs(ms);
    // Drive the editor's playhead from the preview so Split / Add segment /
    // the red playhead line all reflect what the user is looking at.
    setEditorPlayheadMs(ms);
  };
  const handlePreviewPause = () => setPreviewPlaying(false);
  const handlePreviewPlay = () => setPreviewPlaying(true);

  const togglePreviewPlay = () => {
    const v = previewVideoRef.current;
    if (!v) return;
    if (v.paused) {
      // If we're not in active preview mode, start fresh from segment 0
      if (!previewing) { startPreview(); return; }
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } else {
      v.pause();
    }
  };

  // ── Click-to-seek on the timeline ───────────────────────────
  // Seeks BOTH the preview video and the parent recording player, then
  // updates the editor playhead immediately so Split / Add-at-playhead /
  // the red line all reflect the click without waiting for onTimeUpdate.
  const handleTimelineClick = (e) => {
    if (dragStateRef.current || justDraggedRef.current) return;
    const bar = timelineRef.current;
    if (!bar || !durationMs) return;
    const rect = bar.getBoundingClientRect();
    const ratio = clamp((e.clientX - rect.left) / rect.width, 0, 1);
    const ms = ratio * durationMs;
    setEditorPlayheadMs(ms);
    const v = previewVideoRef.current;
    if (v) {
      try { v.currentTime = ms / 1000; } catch (_) { /* ignore */ }
    }
    // Also keep the parent recording player roughly in sync — useful when the
    // user wants to peek at the same moment in the larger player above.
    onSeek?.(ms);
  };

  // ── Save ────────────────────────────────────────────────────
  const handleSave = async () => {
    if (saving) return;
    if (segments.length === 0) {
      toast.error('Add at least one segment first');
      return;
    }
    if (!parentRecordingId || !layout) return;

    setSaving(true);
    try {
      const payload = {
        parentRecordingId,
        layout,
        segments: sortSegments(segments).map((s) => ({
          startMs: Math.round(s.startMs),
          endMs: Math.round(s.endMs),
        })),
        name: name.trim() || undefined,
      };
      const res = await createClip(payload);
      // httpServices auto-unwraps the body — res IS the JSON payload.
      const clip = res?.clip;
      toast.success('Clip queued — will appear in the Clips list shortly');
      onSaved?.(clip);
    } catch (err) {
      console.error('[ClipEditor] createClip failed:', err);
      const msg = err?.response?.data?.message || 'Failed to save clip';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const playheadPct = durationMs > 0 ? (editorPlayheadMs / durationMs) * 100 : 0;
  const safeDurationMs = Math.max(durationMs, 1);

  return (
    <div className="rounded-2xl overflow-hidden mt-4 bg-[#0a1424] border border-white/10 shadow-[0_8px_40px_rgba(0,0,0,0.5)]">
      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          TOP BAR — close • clip name • save
          Mirrors the Twick "RVE / Render Video" header
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <div className="flex items-center gap-3 px-4 py-2.5 bg-[#091022] border-b border-white/10">
        <button
          onClick={onClose}
          className="p-1.5 rounded hover:bg-white/10 text-white/60 hover:text-white"
          title="Close editor"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="w-px h-5 bg-white/10" />
        <label className="flex items-center gap-2 flex-1 min-w-0">
          <span className="text-[11px] uppercase tracking-wider text-white/40 font-semibold flex-shrink-0">Clip name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Untitled clip"
            maxLength={120}
            className="px-3 py-1.5 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#6a1fbf]/60 focus:bg-white/10 flex-1 min-w-0 max-w-md transition-colors"
          />
        </label>
        <button
          onClick={handleSave}
          disabled={saving || segments.length === 0}
          title={
            saving
              ? 'Saving your clip…'
              : segments.length === 0
                ? 'Add at least one segment first'
                : `Save this ${formatTime(totalDurationMs)} clip`
          }
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow"
        >
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          {saving ? 'Saving…' : 'Save clip'}
        </button>
      </div>

      {/* Onboarding hint — dismissible. Saves the dismissed state to
          localStorage so it doesn't re-appear on every visit. */}
      {showHint && (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-gradient-to-r from-[#6a1fbf]/15 to-[#00c853]/10 border-b border-[#6a1fbf]/25 text-xs text-white/80">
          <Lightbulb className="w-4 h-4 text-amber-300 flex-shrink-0" />
          <div className="flex-1">
            <span className="font-medium text-white">Tip: </span>
            Drag the white grips on the green block to set what part of the recording to keep.
            Click <span className="font-semibold text-white">+ Add segment</span> to include another moment, then hit
            <span className="font-semibold text-white"> Save clip</span> when you're done.
          </div>
          <button
            onClick={dismissHint}
            className="p-1 rounded hover:bg-white/10 text-white/50 hover:text-white"
            title="Don't show again"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Settings strip — output / layout in one inline row */}
      <div className="flex items-center gap-2 px-4 py-2 bg-[#0a1424] border-b border-white/10 text-xs">
        <span className="text-white/40 uppercase tracking-wider text-[10px] font-semibold mr-1">Output</span>
        {SIMPLE_OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const active = outputKind === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => setOutputKind(opt.value)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border transition-colors ${
                active
                  ? 'bg-[#6a1fbf]/20 border-[#6a1fbf]/60 text-white'
                  : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10 hover:text-white'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {opt.label}
            </button>
          );
        })}
        {outputKind === 'video' && availableVideoLayouts.length > 1 && (
          <>
            <div className="w-px h-4 bg-white/10 mx-1" />
            <span className="text-white/40 uppercase tracking-wider text-[10px] font-semibold mr-1">Layout</span>
            <button
              onClick={() => setShowLayoutPicker((v) => !v)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 hover:text-white"
            >
              {VIDEO_LAYOUT_LABELS[videoLayout] || videoLayout}
              <span className="text-white/40 text-[10px]">▾</span>
            </button>
            {showLayoutPicker && (
              <div className="flex flex-wrap gap-1.5 ml-1">
                {availableVideoLayouts.filter((l) => l !== videoLayout).map((opt) => {
                  const active = videoLayout === opt;
                  return (
                    <button
                      key={opt}
                      onClick={() => setVideoLayout(opt)}
                      className={`px-2.5 py-1 rounded-md text-[11px] border transition-colors ${
                        active
                          ? 'bg-[#6a1fbf]/25 border-[#6a1fbf]/60 text-white'
                          : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      {VIDEO_LAYOUT_LABELS[opt] || opt}
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          PREVIEW CANVAS — full-width, dark surround. The video frame floats
          on a near-black canvas to give it the dedicated-app NLE feel from
          the Twick/Movavi reference.
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <div className="bg-[#070d1a] py-6 px-6 flex items-center justify-center" style={{ minHeight: 320 }}>
        <div className="relative bg-black rounded-md overflow-hidden shadow-[0_12px_40px_rgba(0,0,0,0.6)] border border-white/5" style={{ width: '100%', maxWidth: 720, aspectRatio: outputKind === 'audio_only' ? '4/1' : '16/9' }}>
          {previewSourceUrl ? (
            <video
              ref={previewVideoRef}
              src={previewSourceUrl}
              onTimeUpdate={handlePreviewTimeUpdate}
              onPlay={handlePreviewPlay}
              onPause={handlePreviewPause}
              playsInline
              className={outputKind === 'audio_only' ? 'hidden' : 'w-full h-full object-contain bg-black'}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-white/40 text-xs">
              No preview source available
            </div>
          )}
          {outputKind === 'audio_only' && previewSourceUrl && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/30">
              <Music className="w-12 h-12" />
              <span className="text-xs uppercase tracking-wider">Audio preview</span>
            </div>
          )}
          {previewing && (
            <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-amber-500/20 border border-amber-500/40 text-amber-200 text-[10px] uppercase tracking-wider font-semibold backdrop-blur-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-300 animate-pulse" />
              Live • {previewIdx + 1} / {previewSortedRef.current.length}
            </span>
          )}
        </div>
      </div>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          PLAYBACK ROW — skip-back, mute, big play, skip-forward, fullscreen
          + centered time display. Mirrors the row directly under the
          preview canvas in the reference editor.
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <div className="flex items-center justify-center gap-3 px-4 py-2.5 bg-[#0a1424] border-t border-white/10">
        <button
          onClick={() => {
            // Jump to start of current segment, or to start of previous segment if already there.
            const sorted = sortSegments(segments);
            if (sorted.length === 0) return;
            const v = previewVideoRef.current;
            const t = (v?.currentTime || 0) * 1000;
            const idx = sorted.findIndex((s) => t >= s.startMs && t <= s.endMs);
            if (idx > 0 && Math.abs(t - sorted[idx].startMs) < 250) {
              const target = sorted[idx - 1];
              if (v) v.currentTime = target.startMs / 1000;
              previewSortedRef.current = sorted; setPreviewing(true); setPreviewIdx(idx - 1);
            } else if (idx >= 0) {
              const target = sorted[idx];
              if (v) v.currentTime = target.startMs / 1000;
              previewSortedRef.current = sorted; setPreviewing(true); setPreviewIdx(idx);
            } else {
              if (v) v.currentTime = sorted[0].startMs / 1000;
              previewSortedRef.current = sorted; setPreviewing(true); setPreviewIdx(0);
            }
          }}
          disabled={!previewSourceUrl || segments.length === 0}
          className="p-2 rounded-md text-white/70 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
          title="Previous segment"
        >
          <SkipBack className="w-4 h-4" />
        </button>
        <button
          onClick={() => {
            setPreviewMuted((m) => {
              const next = !m;
              const v = previewVideoRef.current;
              if (v) v.muted = next;
              return next;
            });
          }}
          disabled={!previewSourceUrl}
          className="p-2 rounded-md text-white/70 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
          title={previewMuted ? 'Unmute' : 'Mute'}
        >
          {previewMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
        <button
          onClick={togglePreviewPlay}
          disabled={!previewSourceUrl || segments.length === 0}
          className="w-11 h-11 rounded-full bg-gradient-to-br from-[#6a1fbf] to-[#5219a3] hover:from-[#7a2acf] hover:to-[#6121b3] flex items-center justify-center text-white disabled:opacity-40 disabled:cursor-not-allowed shadow-lg"
          title={previewPlaying ? 'Pause' : 'Play preview'}
        >
          {previewPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
        </button>
        <div className="px-3 text-sm tabular-nums font-mono text-white/90 whitespace-nowrap min-w-[160px] text-center">
          <span>{formatTime(previewing ? clipElapsedMs(previewSortedRef.current, previewIdx, previewTimeMs) : 0)}</span>
          <span className="text-white/30 mx-1">/</span>
          <span className="text-white/50">{formatTime(totalDurationMs)}</span>
        </div>
        <button
          onClick={() => {
            // Jump to start of next segment.
            const sorted = sortSegments(segments);
            if (sorted.length === 0) return;
            const v = previewVideoRef.current;
            const t = (v?.currentTime || 0) * 1000;
            const idx = sorted.findIndex((s) => t >= s.startMs && t <= s.endMs);
            const nextIdx = idx === -1 ? 0 : Math.min(idx + 1, sorted.length - 1);
            const target = sorted[nextIdx];
            if (v) v.currentTime = target.startMs / 1000;
            previewSortedRef.current = sorted; setPreviewing(true); setPreviewIdx(nextIdx);
          }}
          disabled={!previewSourceUrl || segments.length === 0}
          className="p-2 rounded-md text-white/70 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
          title="Next segment"
        >
          <SkipForward className="w-4 h-4" />
        </button>
        <button
          onClick={() => {
            const v = previewVideoRef.current;
            if (!v) return;
            try {
              if (v.requestFullscreen) v.requestFullscreen();
              else if (v.webkitEnterFullscreen) v.webkitEnterFullscreen();
            } catch (_) { /* ignore */ }
          }}
          disabled={!previewSourceUrl || outputKind === 'audio_only'}
          className="p-2 rounded-md text-white/70 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
          title="Fullscreen"
        >
          <Maximize2 className="w-4 h-4" />
        </button>
      </div>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          EDIT TOOLBAR — only the scissor (split) is functional, so it's the
          only thing rendered. Undo/redo/lock/zoom were visual stubs and have
          been hidden until they're actually wired up.
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <div className="flex items-center gap-1 px-4 py-1.5 bg-[#091022] border-y border-white/10">
        <button
          onClick={() => {
            if (!durationMs) {
              toast.error('No video loaded yet');
              return;
            }
            if (segments.length === 0) {
              toast.error('No segment to split — click "+ Add segment" first');
              return;
            }
            const sorted = sortSegments(segments);
            // Try strict criterion first: playhead is well inside a segment.
            let at = editorPlayheadMs;
            let idx = sorted.findIndex(
              (s) => at >= s.startMs + MIN_SEGMENT_MS && at <= s.endMs - MIN_SEGMENT_MS,
            );
            // Layperson fallback: if the playhead is at exactly 0 (just opened
            // the editor) or stuck on an edge, split the first/active segment
            // right at its midpoint so Split is always productive.
            if (idx === -1) {
              const containing = sorted.findIndex((s) => at >= s.startMs && at <= s.endMs);
              if (containing !== -1) {
                const seg = sorted[containing];
                if (seg.endMs - seg.startMs >= MIN_SEGMENT_MS * 2) {
                  at = Math.round((seg.startMs + seg.endMs) / 2);
                  idx = containing;
                }
              } else if (sorted.length > 0) {
                // Playhead outside all segments — split the longest one at its midpoint.
                const longestIdx = sorted.reduce(
                  (best, s, i) => (s.endMs - s.startMs > sorted[best].endMs - sorted[best].startMs ? i : best),
                  0,
                );
                const seg = sorted[longestIdx];
                if (seg.endMs - seg.startMs >= MIN_SEGMENT_MS * 2) {
                  at = Math.round((seg.startMs + seg.endMs) / 2);
                  idx = longestIdx;
                }
              }
            }
            if (idx === -1) {
              toast.error('Segment too short to split (must be at least 1 second)');
              return;
            }
            const seg = sorted[idx];
            const left = { startMs: seg.startMs, endMs: at };
            const right = { startMs: at, endMs: seg.endMs };
            const realIdx = segments.findIndex((s) => s.startMs === seg.startMs && s.endMs === seg.endMs);
            const next = [...segments];
            if (realIdx !== -1) next.splice(realIdx, 1, left, right);
            setSegments(sortSegments(next));
            // Park the editor playhead at the cut so the user can immediately
            // see where the split happened.
            setEditorPlayheadMs(at);
            const v = previewVideoRef.current;
            if (v) {
              try { v.currentTime = at / 1000; } catch (_) { /* ignore */ }
            }
            toast.success(`Split at ${formatTime(at)}`);
          }}
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-white/70 hover:text-white hover:bg-white/10"
          title="Split — cuts the segment in two at the playhead (or at the segment midpoint if the playhead is at an edge)"
        >
          <Scissors className="w-4 h-4" />
          <span className="text-[11px]">Split</span>
        </button>
      </div>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          TIMELINE — single-track NLE-style. Time ruler on top, track row
          below with grip/lock/trash icons in the left header column.
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <div className="bg-[#0a1424] px-4 pt-3 pb-2">
        <div className="border border-white/10 rounded-lg overflow-hidden bg-[#070d1a]">
          {/* Time ruler */}
          {(() => {
            const tickMs = pickTickIntervalMs(Math.max(durationMs, 1000));
            const ticks = [];
            for (let t = 0; t <= durationMs; t += tickMs) ticks.push(t);
            return (
              <div className="flex border-b border-white/10 bg-[#091022]">
                <div className="w-32 flex-shrink-0 border-r border-white/10" />
                <div
                  className="relative flex-1 h-7 select-none cursor-pointer hover:bg-white/[0.03]"
                  title="Click to position the playhead"
                  onClick={(e) => {
                    if (!durationMs) return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    const ratio = clamp((e.clientX - rect.left) / rect.width, 0, 1);
                    const ms = ratio * durationMs;
                    setEditorPlayheadMs(ms);
                    const v = previewVideoRef.current;
                    if (v) {
                      try { v.currentTime = ms / 1000; } catch (_) { /* ignore */ }
                    }
                    onSeek?.(ms);
                  }}
                >
                  {ticks.map((t) => {
                    const pct = (t / Math.max(durationMs, 1)) * 100;
                    return (
                      <div
                        key={t}
                        className="absolute top-0 bottom-0 flex flex-col items-start"
                        style={{ left: `${pct}%`, transform: t === 0 ? 'translateX(0)' : 'translateX(-50%)' }}
                      >
                        <div className="w-px h-2 bg-white/25" />
                        <div className="text-[10px] text-white/45 font-mono tabular-nums leading-none mt-1 px-1">
                          {formatTime(t)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Video track row */}
          <div className="flex">
            {/* Track header — grip / icon-label / delete-disabled to mirror
                Twick's per-row controls. The Video row can't be deleted (it's
                the only track), so the icons are visual. */}
            <div className="w-32 flex-shrink-0 border-r border-white/10 flex items-center gap-2 px-3 py-3 bg-[#091022]">
              <GripVertical className="w-3.5 h-3.5 text-white/30" />
              <Film className="w-3.5 h-3.5 text-[#a677d9]" />
              <span className="text-[11px] text-white/70 font-medium flex-1">Video</span>
            </div>
            {/* Track lane */}
            <div
              ref={timelineRef}
              onClick={handleTimelineClick}
              className="relative flex-1 h-16 cursor-pointer select-none overflow-hidden"
              title="Click anywhere to seek"
              style={{
                // Subtle vertical gridlines aligned with the ticks above.
                backgroundImage:
                  durationMs > 0
                    ? `repeating-linear-gradient(90deg, rgba(255,255,255,0.04) 0, rgba(255,255,255,0.04) 1px, transparent 1px, transparent ${(pickTickIntervalMs(Math.max(durationMs, 1000)) / Math.max(durationMs, 1)) * 100}%)`
                    : undefined,
              }}
            >
          {/* Segment blocks */}
          {segments.map((seg, i) => {
            const leftPct = (seg.startMs / safeDurationMs) * 100;
            const widthPct = ((seg.endMs - seg.startMs) / safeDurationMs) * 100;
            // Highlight the segment currently playing during preview so the
            // user can see which range is on screen at any moment.
            const isPreviewing =
              previewing &&
              previewIdx >= 0 &&
              previewSortedRef.current[previewIdx] &&
              previewSortedRef.current[previewIdx].startMs === seg.startMs &&
              previewSortedRef.current[previewIdx].endMs === seg.endMs;
            return (
              <div
                key={i}
                onMouseDown={(e) => beginDrag(i, 'move', e)}
                onClick={(e) => e.stopPropagation()}
                className={`absolute top-0 bottom-0 cursor-grab active:cursor-grabbing select-none group/seg overflow-hidden ${
                  isPreviewing
                    ? 'border-2 border-amber-300 ring-2 ring-amber-300/50'
                    : 'border border-emerald-400/70'
                }`}
                style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                title={`Drag to move • ${formatTime(seg.startMs)} → ${formatTime(seg.endMs)}`}
              >
                {/* Filmstrip thumbnails as the visual fill (falls back to a
                    purple→green gradient if extraction is still running or
                    the video can't be canvas-read). */}
                <ThumbnailStrip
                  thumbnails={thumbnails}
                  startMs={seg.startMs}
                  endMs={seg.endMs}
                  totalDurationMs={durationMs}
                  loading={thumbsLoading}
                  progress={thumbsProgress}
                />
                {/* Subtle tint overlay so the segment reads as "selected"
                    rather than just raw video frames. */}
                <div className="absolute inset-0 bg-emerald-500/15 mix-blend-overlay pointer-events-none" />
                {/* Start handle — wider hit area + grip lines so it's
                    obvious you can drag it. */}
                <div
                  onMouseDown={(e) => beginDrag(i, 'start', e)}
                  onClick={(e) => e.stopPropagation()}
                  className="absolute top-0 bottom-0 -left-2 w-4 cursor-ew-resize flex items-center justify-center bg-white hover:bg-white rounded-l-md shadow-md z-10"
                  title="Drag to adjust start"
                >
                  <div className="flex flex-col gap-0.5">
                    <div className="w-0.5 h-3 bg-[#001233]/70 rounded" />
                    <div className="w-0.5 h-3 bg-[#001233]/70 rounded" />
                  </div>
                </div>
                {/* End handle */}
                <div
                  onMouseDown={(e) => beginDrag(i, 'end', e)}
                  onClick={(e) => e.stopPropagation()}
                  className="absolute top-0 bottom-0 -right-2 w-4 cursor-ew-resize flex items-center justify-center bg-white hover:bg-white rounded-r-md shadow-md z-10"
                  title="Drag to adjust end"
                >
                  <div className="flex flex-col gap-0.5">
                    <div className="w-0.5 h-3 bg-[#001233]/70 rounded" />
                    <div className="w-0.5 h-3 bg-[#001233]/70 rounded" />
                  </div>
                </div>
                {/* Index pill */}
                <div className="absolute top-1 left-2 text-[9px] font-bold text-white bg-black/40 rounded px-1 leading-tight pointer-events-none">
                  {i + 1}
                </div>
                {/* Duration label — visible inside the segment so users see at
                    a glance how long this slice is. Hides if the segment is
                    too narrow to fit text. */}
                {((seg.endMs - seg.startMs) / safeDurationMs) > 0.06 && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className="text-[10px] font-mono tabular-nums font-semibold text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                      {formatTime(seg.endMs - seg.startMs)}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
          {/* Playhead */}
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-red-400 pointer-events-none"
            style={{ left: `${playheadPct}%` }}
          />
          {/* Empty-state guidance when the track has no segments at all. */}
          {segments.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="text-[11px] text-white/40 italic">
                No segments — click the <span className="font-semibold text-white/60">+ Add segment</span> button below to get started
              </span>
            </div>
          )}
          {/* Live drag tooltip — floats above the active handle showing the
              snapped time, so the user can see exactly where they're cutting. */}
          {dragTip && (
            <div
              className="absolute -top-7 px-2 py-0.5 rounded bg-black/90 border border-white/20 text-[11px] text-white font-mono tabular-nums shadow-lg pointer-events-none whitespace-nowrap"
              style={{ left: `${dragTip.leftPct}%`, transform: 'translateX(-50%)' }}
            >
              {dragTip.label}
            </div>
          )}
            </div>
          </div>

          {/* Generating-thumbnails status — surfaces the progress bar at the
              very bottom of the video track so users on long recordings know
              the editor is alive while extraction runs. */}
          {thumbsLoading && (
            <div className="flex border-t border-white/10 bg-[#0a1424]">
              <div className="w-32 flex-shrink-0 border-r border-white/10 px-3 py-1.5 flex items-center gap-2 bg-[#091022]">
                <Loader2 className="w-3 h-3 text-amber-300 animate-spin" />
                <span className="text-[10px] text-white/50">Loading frames</span>
              </div>
              <div className="flex-1 relative h-6">
                <div
                  className="absolute top-1/2 -translate-y-1/2 left-3 right-3 h-1 rounded-full bg-white/10 overflow-hidden"
                >
                  <div
                    className="h-full bg-gradient-to-r from-[#6a1fbf] to-[#00c853] transition-[width] duration-200"
                    style={{ width: `${Math.round((thumbsProgress || 0) * 100)}%` }}
                  />
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          BOTTOM STATUS BAR — segment count + add button. Save lives in the
          top bar (mirrors the Twick "Render Video" placement).
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <div className="flex items-center gap-3 px-4 py-2.5 bg-[#091022] border-t border-white/10 text-xs">
        <button
          onClick={addSegmentAtPlayhead}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 hover:text-white"
          disabled={!durationMs}
          title="Add a new segment at the playhead position"
        >
          <Plus className="w-3.5 h-3.5" />
          Add segment
        </button>
        {segments.length > 0 && (
          <button
            onClick={() => {
              if (window.confirm('Remove all segments and start over?')) {
                stopPreview();
                setSegments([]);
              }
            }}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-white/50 hover:text-white hover:bg-white/10"
            title="Remove all segments and start over"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Start over
          </button>
        )}
        <div className="w-px h-5 bg-white/10" />
        <span className="text-white/50">
          {segments.length === 0
            ? 'No segments yet'
            : segments.length === 1
              ? '1 segment'
              : `${segments.length} segments`}
        </span>
        <span className="text-white/30">•</span>
        <span className="text-white/50">
          Length: <span className="text-white/80 font-mono tabular-nums">{formatTime(totalDurationMs)}</span>
        </span>
        <div className="flex-1" />
        {previewing && (
          <button
            onClick={stopPreview}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-500/15 border border-amber-500/40 text-amber-200 hover:bg-amber-500/25"
            title="Stop preview"
          >
            <Square className="w-3.5 h-3.5" />
            Stop preview
          </button>
        )}
        <button
          onClick={onClose}
          disabled={saving}
          className="px-3 py-1 rounded-md text-white/60 hover:text-white hover:bg-white/10"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving || segments.length === 0}
          title={
            saving
              ? 'Saving your clip…'
              : segments.length === 0
                ? 'Add at least one segment first'
                : `Save this ${formatTime(totalDurationMs)} clip`
          }
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow"
        >
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          {saving ? 'Saving…' : 'Save clip'}
        </button>
      </div>
    </div>
  );
}
